/* ============================================================
 * Datos del cliente y confirmación de la reserva.
 *
 * ⚠ El móvil NO se verifica. Se pide porque es la identidad del cliente en
 * todo el sistema —es lo que enlaza sus visitas y sus bonos—, pero nada
 * comprueba que sea suyo. Comprobarlo exigiría enviarle un código, y eso
 * queda para la v2.
 *
 * La garantía de la v1 es el pase que se emite al confirmar: la sede lo
 * valida cuando la persona se presenta. Lo que se pierde por el camino es
 * la autenticidad de la reserva; lo que se gana es no depender de un
 * proveedor externo para poder reservar.
 * ============================================================ */

import React, { useState } from 'react';
import { api } from '../api.js';
import { clp, fechaLarga, normEmail, normTel } from '../utiles/fechas.js';

export function Identify({ sede, date, time, onListo, onBack }) {
  const s = sede;
  const [telefono, setTelefono] = useState('');
  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [codigo, setCodigo] = useState('');
  const [promo, setPromo] = useState(null);   // respuesta de la vista previa
  const [error, setError] = useState('');
  const [enCurso, setEnCurso] = useState(false);

  const [validando, setValidando] = useState(false);

  // El descuento se aplica cuando la persona pulsa «Validar», no antes: un
  // precio que cambia solo al perder el foco parece un fallo, no una promo.
  // La palabra final la tiene el backend al confirmar, que valida y consume.
  const comprobarCodigo = async () => {
    const c = codigo.trim();
    if (!c) { setPromo(null); return; }
    setValidando(true);
    try { setPromo(await api.codigo(s.id, c)); }
    catch { setPromo({ ok: false, motivo: 'No se pudo comprobar el código. Inténtalo de nuevo.' }); }
    finally { setValidando(false); }
  };

  const confirmar = async () => {
    if (!nombre.trim()) { setError('¿Cómo te llamas?'); return; }
    const tel = normTel(telefono);
    if (!tel) { setError('Escribe un móvil chileno válido (+56 9 ...).'); return; }
    const mail = normEmail(email);
    if (!mail) { setError('Escribe un email válido: ahí te llega la confirmación.'); return; }
    // Un código escrito pero sin validar no se aplica en silencio.
    if (codigo.trim() && !promo?.ok) {
      setError('Valida el código promocional, o deja el campo vacío.');
      return;
    }

    setError('');
    setEnCurso(true);
    try {
      // El hueco puede haberse ocupado mientras se rellenaba el formulario;
      // en ese caso el backend lo dice y se muestra tal cual.
      const reserva = await api.reservar({
        sedeId: s.id, fecha: date, hora: time, nombre: nombre.trim(), telefono: tel, email: mail,
        codigoPromocional: promo?.ok ? codigo.trim() : undefined,
      });
      onListo({ nombre: nombre.trim(), equipo: reserva.equipo, pase: reserva.pase,
                precioFinal: reserva.precioFinal });
    } catch (e) {
      setError(e.message);
    } finally {
      setEnCurso(false);
    }
  };

  return (
    <div className="wrap flow">
      <div className="flow-eyebrow">Último paso</div>
      <h1 className="flow-title">Tus datos</h1>
      <p className="flow-sub">
        Sin cuentas ni contraseñas. Al confirmar te damos un pase para enseñar
        al llegar, y te enviamos la confirmación a tu correo.
      </p>

      <div className="resumen">
        <div className="res-row"><span className="k">Centro</span><span className="v">{s.nombre}</span></div>
        <div className="res-row"><span className="k">Día</span><span className="v">{fechaLarga(date)}</span></div>
        <div className="res-row"><span className="k">Hora</span><span className="v">{time}</span></div>
        <div className="res-row"><span className="k">Sesión</span><span className="v">
          {promo?.ok
            ? <><s style={{ opacity: 0.55 }}>{clp(s.precio_tune)}</s> {clp(promo.precioFinal)} · en el centro</>
            : <>{clp(s.precio_tune)} · en el centro</>}
        </span></div>
      </div>

      <div className="f-field"><label>Nombre</label>
        <input value={nombre} onChange={(e) => setNombre(e.target.value)}
               placeholder="Tu nombre" autoFocus /></div>
      <div className="f-field"><label>Móvil</label>
        <input value={telefono} onChange={(e) => setTelefono(e.target.value)}
               type="tel" placeholder="+56 9 ..." /></div>
      <div className="f-field"><label>Email</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)}
               type="email" placeholder="tu@correo.cl" /></div>
      <div className="f-field"><label>Código promocional (opcional)</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={codigo} style={{ flex: 1 }}
                 onChange={(e) => { setCodigo(e.target.value.toUpperCase()); setPromo(null); }}
                 placeholder="¿Tienes un código?"
                 onKeyDown={(e) => { if (e.key === 'Enter') comprobarCodigo(); }} />
          <button className="btn" type="button" onClick={comprobarCodigo}
                  disabled={validando || !codigo.trim()}>
            {validando ? 'Validando…' : 'Validar'}
          </button>
        </div></div>
      {promo ? (
        <div className={promo.ok ? 'promo-ok' : 'err'} style={promo.ok ? { color: '#2d5a4c', fontSize: 14 } : undefined}>
          {promo.ok
            ? `Descuento aplicado: pagarás ${clp(promo.precioFinal)} en el centro.`
            : promo.motivo}
        </div>
      ) : null}

      <div className="err">{error}</div>

      <button className="btn block" onClick={confirmar} disabled={enCurso}>
        {enCurso ? 'Confirmando…' : 'Confirmar reserva'}
      </button>
      <button className="link-btn" onClick={onBack}>‹ Cambiar horario</button>
    </div>
  );
}
